import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { colors, typography } from '../../../styles/theme';
import { applyCadPlanNavigationLimits, useCadPlanMap } from '../../hooks/useCadPlanMap';
import { useCadParcelsLayer } from '../../hooks/useCadParcelsLayer';
import {
  computeFeatureCollectionBbox,
  projectCadPlanCollection,
  readCadPlanBounds,
} from '../../utils/cadGeometry';
import { previewStageSx } from './CadUploadFlow.styles';
import MapUnavailable from '../../../Map/components/MapUnavailable';

const FIT_PADDING = { top: 16, bottom: 16, left: 16, right: 16 };
const FIT_MAX_ZOOM = 22;

/**
 * Miniatura (o preview grande) del plano en metros (`srid: 0`) para mirar
 * antes de confirmar. Reusa el lienzo canvas del showroom sin controles ni
 * selección: es una vista de revisión, no un mapa operable.
 *
 * @param {{
 *   featureCollection: object|null,
 *   bounds?: unknown,
 *   height?: number,
 *   overlay?: React.ReactNode,
 * }} props
 */
export default function CadPlanPreview({ featureCollection, bounds, height = 220, overlay = null }) {
  const planCollection = useMemo(
    () => projectCadPlanCollection(
      featureCollection,
      readCadPlanBounds({ bounds }, featureCollection),
    ),
    [bounds, featureCollection],
  );
  const hasGeometry = planCollection.features.some((feature) => feature?.geometry);

  const {
    mapRef,
    mapInstanceRef,
    mapReady,
    mapUnavailable,
    mapUnavailableReason,
  } = useCadPlanMap({ enabled: hasGeometry, surface: 'cad_upload_preview' });

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady || !hasGeometry) return;
    applyCadPlanNavigationLimits(map, computeFeatureCollectionBbox(planCollection));
  }, [hasGeometry, mapInstanceRef, mapReady, planCollection]);

  useCadParcelsLayer({
    mapInstanceRef,
    enabled: hasGeometry && mapReady,
    featureCollection: planCollection,
    selectedId: null,
    onSelect: undefined,
    fitPadding: FIT_PADDING,
    fitOnLoad: true,
    fitMaxZoom: FIT_MAX_ZOOM,
    pickEnabled: false,
  });

  if (!hasGeometry) {
    return (
      <Box sx={{
        ...previewStageSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        height,
      }}
      >
        <Typography sx={{
          color: colors.textSecondary,
          fontSize: '13px',
          fontFamily: typography.fontFamily.primary,
          textAlign: 'center',
        }}
        >
          No pudimos dibujar el plano. Revisá el archivo antes de crear la lista.
        </Typography>
        {overlay}
      </Box>
    );
  }

  if (mapUnavailable) {
    return (
      <Box sx={{ ...previewStageSx, height }}>
        <MapUnavailable variant="stage" reason={mapUnavailableReason} compact />
        {overlay}
      </Box>
    );
  }

  return (
    <Box sx={{ ...previewStageSx, height }}>
      <div
        ref={mapRef}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />
      {!mapReady && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.backgroundDark,
        }}
        >
          <CircularProgress size={22} sx={{ color: 'primary.main' }} />
        </Box>
      )}
      {overlay}
    </Box>
  );
}
